import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'
import accorv2 from 'assets/company-accorv2.webp'
import carrefour from 'assets/company-carrefour.webp'
import engie from 'assets/company-engie.webp'
import fdj from 'assets/company-fdj.webp'
import lequipe from 'assets/company-lequipe.webp'
import maif from 'assets/company-maif.webp'
import renault from 'assets/company-renault.webp'
import respire from 'assets/company-respire.webp'
import swile from 'assets/company-swile.webp'
import wonderbox from 'assets/company-wonderbox.webp'
import hero from 'assets/home-hero.webp'
import { Button } from 'components/Button'
import type { IconKeys } from '@captaincause/ui/Icon'
import type { StaticImageData } from 'next/image'

const COMPANIES = [
  {
    image: renault,
    alt: 'Renault logo 2',
    imageStyle: '',
  },
  {
    image: fdj,
    alt: 'Française des Jeux logo',
    imageStyle: '',
  },
  {
    image: respire,
    alt: 'Respire logo',
    imageStyle: '',
  },
  {
    image: wonderbox,
    alt: 'Wonderbox logo',
    imageStyle: '',
  },
  {
    image: lequipe,
    alt: 'Lequipe logo',
    imageStyle: '',
  },
  {
    image: maif,
    alt: 'Maif logo',
    imageStyle: '',
  },
  {
    image: engie,
    alt: 'ENGIE logo',
    imageStyle: '',
  },
  {
    image: accorv2,
    alt: 'Accor logo',
    imageStyle: '',
  },
  {
    image: swile,
    alt: 'Swile logo',
    imageStyle: '',
  },
  {
    image: carrefour,
    alt: 'Carrefour logo',
    imageStyle: '',
  },
  {
    image: renault,
    alt: 'Renault logo',
    imageStyle: '',
  },
  {
    image: maif,
    alt: 'Maif logo 2',
    imageStyle: '',
  },
  {
    image: fdj,
    alt: 'Française des Jeux logo 2',
    imageStyle: '',
  },
  {
    image: lequipe,
    alt: 'Lequipe logo 2',
    imageStyle: '',
  },
]

interface ImpactItem {
  color: string
  icon: IconKeys
  content: string
}

interface ImageItem {
  image: StaticImageData
  alt: string
}

export function Hero() {
  const { t } = useTranslation('home.page')

  const firstRow = useRef<HTMLDivElement>(null)
  const secondRow = useRef<HTMLDivElement>(null)
  const scrollCoeff = 5

  useEffect(() => {
    let latestKnownScrollY = 0
    let ticking = false

    function update() {
      ticking = false
      if (firstRow.current) {
        firstRow.current.style.transform = `translate3d(${
          latestKnownScrollY / scrollCoeff
        }px,0,0)`
      }
      if (secondRow.current) {
        secondRow.current.style.transform = `translate3d(-${
          latestKnownScrollY / scrollCoeff
        }px,0,0)`
      }
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(update)
      }
      ticking = true
    }

    function onScroll() {
      latestKnownScrollY = window.scrollY
      if (latestKnownScrollY < 1000) requestTick()
    }

    window.addEventListener('scroll', onScroll, false)

    return () => {
      window.addEventListener('scroll', onScroll, false)
    }
  }, [])

  return (
    <div className="overflow-hidden bg-light-beige">
      <div className="mx-auto flex max-w-screen-2xl flex-col-reverse items-center justify-center gap-2 px-14 py-10 md:flex-row md:pb-10 xl:pt-16">
        <div className="flex flex-col items-center lg:items-start">
          <h1 className="mb-4 max-w-[400px] text-center text-4xl font-semibold leading-[40px] -tracking-[2px] text-black md:mb-8 md:max-w-[540px] md:text-[50px] md:leading-[50px] md:-tracking-[2.24px] lg:text-left xl:text-[56px] xl:leading-[56px] xl:-tracking-[2.24px]">
            {t('hero.title1')} {t('hero.title2')}
          </h1>
          <p className="mb-8 max-w-[400px] text-center text-base text-dark-gray md:max-w-[500px] md:text-lg lg:text-left">
            {t('hero.subtitle')}
          </p>
          <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
            <Button
              href="/contact"
              variant="primary"
              className="h-12 w-[240px] whitespace-nowrap rounded-full px-7 py-3 font-semibold md:h-13"
            >
              {t('hero.contact')}
            </Button>
            <Button
              href="/cadeau"
              variant="secondary"
              locale={false}
              className="h-12 w-[240px] rounded-full border-gray px-7 py-3 font-semibold md:h-13"
            >
              {t('hero.difts')}
            </Button>
          </div>
        </div>
        <div className="relative h-[187px] w-[250px] sm:h-[263px] sm:w-[350px] md:hidden lg:block lg:h-[446px] lg:w-[580px] xl:h-[480px] xl:w-[640px]">
          <Image
            src={hero}
            alt="Dift hero main page"
            fill
            className="object-contain"
          />
        </div>
      </div>
      <div className="relative mb-10 h-[90px] w-screen overflow-scroll">
        <div
          ref={firstRow}
          className="absolute -left-[90px] top-0 flex w-fit gap-6 md:gap-14"
        >
          {COMPANIES.map(company => (
            <Image
              key={company.alt}
              src={company.image}
              alt={company.alt}
              className="h-[75px] max-h-[75px] w-max max-w-[100px] object-contain md:max-w-[160px]"
            />
          ))}
        </div>
      </div>
    </div>
  )
}
